import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, RotateCcw, Mail, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = sessionStorage.getItem("micah_session");
  if (!id) {
    id = "micah-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("micah_session", id);
  }
  return id;
};

const GREETING = {
  role: "assistant",
  text: "Hello! I'm Micah, the virtual assistant for Modern Business Solutions. It's lovely to meet you. How may I help you today?",
};

const MAIN_OPTIONS = [
  { id: "english-test", label: "Register for the English Test (Work Permit)" },
  { id: "app-dev", label: "I need an app for my business" },
  { id: "other", label: "Something else" },
];

const LOCATION_OPTIONS = [
  { id: "grand-cayman", label: "Grand Cayman" },
  { id: "sister-islands", label: "Sister Islands (Cayman Brac or Little Cayman)" },
];

export default function MicahAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [mode, setMode] = useState("menu"); // menu | location | app | chat
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking, open]);

  const addMsg = (role, text) => setMessages((prev) => [...prev, { role, text }]);

  const resetToMenu = () => {
    setMode("menu");
    addMsg("assistant", "Of course! How else may I help you today?");
  };

  const handleMainOption = (opt) => {
    addMsg("user", opt.label);
    if (opt.id === "english-test") {
      setMode("location");
      addMsg(
        "assistant",
        "Wonderful — I'd be happy to help you register. May I ask, are you in Grand Cayman or the Sister Islands (Cayman Brac or Little Cayman)?"
      );
    } else if (opt.id === "app-dev") {
      setMode("app");
      addMsg(
        "assistant",
        "That's exciting! Our team builds custom mobile apps, web apps and business systems right here in the Cayman Islands. The best next step is to book a consult — just share a few details on our contact form and we'll be in touch promptly."
      );
    } else {
      setMode("chat");
      addMsg(
        "assistant",
        "Certainly! Please type your question below and I'll do my very best to help you."
      );
    }
  };

  const handleLocation = (opt) => {
    addMsg("user", opt.label);
    if (opt.id === "grand-cayman") {
      addMsg(
        "assistant",
        "Perfect! I'm taking you to the registration page for the Redesigned TOEIC Bridge Listening & Reading Test now. Simply pick your date and time, and I wish you the very best on your test!"
      );
      setTimeout(() => {
        navigate("/book?test=bridge-lr");
        setOpen(false);
        setMode("menu");
      }, 1800);
    } else {
      setMode("sister");
      addMsg(
        "assistant",
        "Thank you! For testing in the Sister Islands, please email our Test Administrator at ELT.BRAC@GMAIL.COM and they will kindly schedule your test for you."
      );
    }
  };

  const sendChat = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    setInput("");
    addMsg("user", text);
    setThinking(true);
    try {
      const res = await axios.post(`${API}/assistant/chat`, {
        session_id: getSessionId(),
        message: text,
      });
      addMsg("assistant", res.data.reply);
    } catch {
      addMsg(
        "assistant",
        "I'm so sorry — I'm having a little trouble right now. Please use our contact form on the Contact page or call 1-345-924-6887 and our team will gladly help you."
      );
    } finally {
      setThinking(false);
    }
  };

  return (
    <div className="print:hidden">
      {/* Floating button */}
      {!open && (
        <button
          data-testid="micah-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold pl-4 pr-5 py-3.5 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5"
        >
          <MessageCircle size={22} />
          <span className="hidden sm:block">Chat with Micah</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="micah-panel"
          className="fixed bottom-6 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] bg-white rounded-2xl shadow-2xl border border-slate-100 flex flex-col overflow-hidden"
          style={{ maxHeight: "min(600px, calc(100vh - 3rem))" }}
        >
          {/* Header */}
          <div className="bg-[#0A2540] px-5 py-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0E9AAA] flex items-center justify-center text-white font-extrabold">
                M
              </div>
              <div>
                <div className="text-white font-extrabold leading-tight">Micah</div>
                <div className="text-[#39C1D0] text-xs font-semibold">MBS Virtual Assistant</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setMessages([GREETING]);
                  setMode("menu");
                }}
                title="Start over"
                className="p-2 text-slate-300 hover:text-white transition-colors"
                data-testid="micah-reset-btn"
              >
                <RotateCcw size={16} />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-2 text-slate-300 hover:text-white transition-colors"
                data-testid="micah-close-btn"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F6FAFB]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0E9AAA] text-white rounded-br-md"
                      : "bg-white text-slate-700 border border-slate-100 shadow-sm rounded-bl-md"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-100 shadow-sm rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-slate-400 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Micah is typing...
                </div>
              </div>
            )}

            {/* Quick options */}
            {mode === "menu" && !thinking && (
              <div className="space-y-2 pt-1">
                {MAIN_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    data-testid={`micah-option-${opt.id}`}
                    onClick={() => handleMainOption(opt)}
                    className="w-full text-left bg-white border border-[#B5E6EC] hover:border-[#0E9AAA] hover:bg-[#E8F7F9] text-[#0A2540] font-semibold text-sm rounded-xl px-4 py-3 transition-colors"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            {mode === "location" && !thinking && (
              <div className="space-y-2 pt-1">
                {LOCATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    data-testid={`micah-location-${opt.id}`}
                    onClick={() => handleLocation(opt)}
                    className="w-full text-left bg-white border border-[#B5E6EC] hover:border-[#0E9AAA] hover:bg-[#E8F7F9] text-[#0A2540] font-semibold text-sm rounded-xl px-4 py-3 transition-colors"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            {mode === "sister" && (
              <div className="space-y-2 pt-1">
                <a href="mailto:ELT.BRAC@GMAIL.COM" className="block">
                  <Button className="w-full bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-xl">
                    <Mail size={15} className="mr-2" /> Email ELT.BRAC@GMAIL.COM
                  </Button>
                </a>
                <button
                  onClick={resetToMenu}
                  className="w-full text-center text-xs font-bold text-slate-400 hover:text-[#0E9AAA] py-1.5 transition-colors"
                >
                  Back to main menu
                </button>
              </div>
            )}
            {mode === "app" && (
              <div className="space-y-2 pt-1">
                <Button
                  data-testid="micah-consult-btn"
                  onClick={() => {
                    navigate("/contact");
                    setOpen(false);
                    setMode("menu");
                  }}
                  className="w-full bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-xl"
                >
                  Book a Consult <ArrowRight size={15} className="ml-2" />
                </Button>
                <button
                  onClick={resetToMenu}
                  className="w-full text-center text-xs font-bold text-slate-400 hover:text-[#0E9AAA] py-1.5 transition-colors"
                >
                  Back to main menu
                </button>
              </div>
            )}
            {mode === "chat" && !thinking && (
              <button
                onClick={resetToMenu}
                className="w-full text-center text-xs font-bold text-slate-400 hover:text-[#0E9AAA] py-1 transition-colors"
              >
                Back to main menu
              </button>
            )}
          </div>

          {/* Input (free chat mode) */}
          {mode === "chat" && (
            <div className="border-t border-slate-100 p-3 flex gap-2 bg-white shrink-0">
              <Input
                data-testid="micah-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendChat()}
                placeholder="Type your question..."
                className="rounded-xl"
              />
              <Button
                data-testid="micah-send-btn"
                onClick={sendChat}
                disabled={thinking || !input.trim()}
                className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white rounded-xl px-4 shrink-0"
              >
                <Send size={16} />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
